import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {Router} from '@angular/router';
import {StateService} from 'src/app/core/services/state/state.service';
import {UserPreferencesService} from 'src/app/administration/services/user-preferences.service';
import {ComponentPrimitive} from 'src/app/shared/classes/components/componentPrimitive';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {DeviceDetectorService} from 'ngx-device-detector';
import {JobsListService} from '../../../services/jobs-list.service';
import {JobsSearchFiltersService} from '../../../services/jobs-search-filters.service';
import {Subscription} from 'rxjs';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { CustomDateAdapter, CUSTOM_DATE_FORMATS } from '../../../../shared/helpers/custom-date-adapter';
import { MAT_DATE_FORMATS } from '@angular/material/core';

@Component({
    selector: 'app-jobs-list-search-filters',
    templateUrl: './jobs-list-search-filters.component.html',
    styleUrls: ['./jobs-list-search-filters.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobsListSearchFiltersComponent extends ComponentPrimitive implements OnInit, OnDestroy {

    @Input() whereAmI: string;
    @Input() width: string;
    @Output() selectPreference = new EventEmitter;

    targetsOptions = [];
    targetsModel = [];

    preferences = false;
    userPreferences: any;

    preferenceUrl: string;

    accountDataId: string;
    userDataId: string;

    private subscriptions = new Subscription();

    constructor(
        public stateService: StateService,
        private userPreferencesService: UserPreferencesService,
        private changeDetectorRef: ChangeDetectorRef,
        private dateAdapter: DateAdapter<Date>,
        public translate: TranslateService,
        public jobsList: JobsListService,
        public jobSearchFilterService: JobsSearchFiltersService,
        protected router: Router,
        protected deviceService: DeviceDetectorService,
    ) {
        super(
            stateService,
            translate,
            router,
            deviceService
        );
    }

    ngOnInit() {
        this.jobSearchFilterService.initDateFormat(this.dateFormat());
        this.jobSearchFilterService.setSubAccountsOptions();
        this.jobSearchFilterService.setDepartmentsOptions();

        this.subscriptions.add(this.translate.onLangChange.subscribe(() => {
                    this.jobSearchFilterService.initDateFormat(this.dateFormat());
                    this.checkPeriodDates();
                    this.initTargets();
                    this.changeDetectorRef.markForCheck();
                })
        );

        this.jobsList.checkModuleUrl();
        this.initTargets();
        this.jobSearchFilterService.checkPreferences(this.whereAmI);

        if (this.whereAmI === 'filter') {
            this.jobSearchFilterService.initInputState();
        }

        if (this.whereAmI === 'preferences') {
            let accountDataId = (this.jobsList.accountData) ? this.jobsList.accountData.id : '';
            this.userDataId = (this.jobsList.userData) ? this.jobsList.userData.id : '';
            this.preferenceUrl = 'admin/account/' + accountDataId + '/user/' + this.userDataId + '/preferences';

            // Inject info in state
            this.userPreferencesService.setUserPreferences(this.jobsList.userPreferences, this.preferenceUrl);

            // Init state with session vars
            this.jobSearchFilterService.initInputStatePreferences(this);
            this.changeDetectorRef.markForCheck();
        }

        this.jobSearchFilterService.initSubAccountFilter();

        this.checkPeriodDates();

        this.jobSearchFilterService.initColumnsFilter();

        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.dateAdapter.setLocale(event.lang);
        });
    }

    initTargets() {
        const names = this.jobsList.columnsName[this.translate.currentLang];
        this.targetsOptions = [
            {id: 'personality', name: names.targetPersonality},
            {id: 'competencies', name: names.targetTalents},
            {id: 'cai', name: names.targetIac}
        ];
        const saved = this.jobsList.jobsState.listSearchFilters.targets || [];
        const ids = typeof saved === 'string' ? saved.split(',') : saved;
        this.targetsModel = this.targetsOptions.filter(option => ids.includes(option.id));
    }

    setTargets(selected) {
        this.jobsList.jobsState.listSearchFilters.targets = selected.map(option => option.id);
        this.jobsList.getJobList(true);
    }

    resetSearchFiltersAction(preferences = null) {
        this.jobSearchFilterService.resetSearchFiltersAction();

        if (this.whereAmI === 'filter') {
            this.jobSearchFilterService.resetSearchFilters();
        }
        for (let report in this.stateService[this.jobsList.specificState].listSearchFilters) {
            if (report && this.stateService[this.jobsList.specificState].listSearchFilters.hasOwnProperty(report)) {
                this.stateService[this.jobsList.specificState].listColumns = this.stateService[this.jobsList.specificState].listColumnsInitialConfig;
                if (report !== 'search') {
                    this.stateService[this.jobsList.specificState].listSearchFilters[report] = null;
                }
            }
        }

        if (preferences === null) {
            this.jobsList.setInitialColumnsList();
            this.jobsList.listColumns = JSON.parse(JSON.stringify(this.jobsList.listColumnsInitialConfig));
        } else {
            // Inject info in state
            this.userPreferencesService.setUserPreferences(this.jobsList.userPreferences, this.preferenceUrl, 'jobs');
            this.jobSearchFilterService.initInputStatePreferences(this);
        }

        this.initTargets();
        this.selectPreference.emit({
            type: 'selectVanilla'
        });
    }

    checkPeriodDates() {
        if (this.jobsList.evaluationStartDate) {
            let dateStartObject = new Date(this.jobsList.jobsStartDate);
            this.jobSearchFilterService.dateStartModel = dateStartObject;
            this.jobSearchFilterService.dateStart = dateStartObject;
        }

        if (this.jobsList.evaluationEndDate) {
            let dateEndObject = new Date(this.jobsList.jobsEndDate);
            this.jobSearchFilterService.dateEndModel = dateEndObject;
            this.jobSearchFilterService.dateEnd = dateEndObject;
        }
    }

    resetSearchFilters() {
        if (this.whereAmI === 'filter' && this.actualModule.name !== 'jobs') {
            const preferences = (this.userPreferencesService.isPreferenceSet('jobs')) ?
                this.stateService.session.preferences['jobs'] :
                null;
            this.resetSearchFiltersAction(preferences);
        } else {
            this.resetSearchFiltersAction();
            this.userPreferencesService.emptyUserPreferences('jobs', this.userDataId);
        }
        this.jobsList.getJobList(true);
    }

    setFilters(event: MatDatepickerInputEvent<Date>, filter) {
        switch (filter) {
            case 'jobStartDate':
                this.jobSearchFilterService.setSearchFilter(event.value, 'jobStartDate');
                break;
            case 'jobEndDate':
                this.jobSearchFilterService.setSearchFilter(event.value, 'jobEndDate');
                break;
            case 'subAccount':
                let subAccounts = this.checkDropDownOptions(event);
                this.checkDropDownPlaceholder(subAccounts, 'subAccountCol', this.translate.instant('commons.subAccount'));
                this.jobSearchFilterService.subAccount(subAccounts, this, this.jobsList.specificState);
                break;
            case 'department':
                let deps = this.checkDropDownOptions(event);
                this.checkDropDownPlaceholder(deps, 'depCol', this.translate.instant('commons.division'));
                this.jobSearchFilterService.onDepartmentOptionChange(deps, this, this.jobsList.specificState);
                break;
            case 'columnDisplay':
                this.jobSearchFilterService.columnDisplay(event, this, this.jobsList.specificState);
                break;
        }
        this.jobsList.getJobList(true);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
